/**
 * Logística → Ventas Market (`/logistica/ventas-market`).
 *
 * Puerto de `Logistics::market/market_data` + `views/market/index.php`: las
 * ventas de productos del market con sus filtros (fechas, número de documento y
 * cliente), las mismas columnas (documento, cliente, productos, pagos, total y
 * estado) y la edición de los métodos de pago.
 *
 * La **anulación** del comprobante ante SUNAT depende del servicio externo de
 * CPE (`api_dir_empresa` + token), igual que en Documentos, así que por ahora se
 * muestra con un candado y el motivo del bloqueo cuando la fila no cumple las
 * condiciones (fuera del plazo de días o turno sin verificar).
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Filter,
  Lock,
  Pencil,
  RotateCcw,
  ShieldAlert,
  ShoppingCart,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { EditarPagosDialogo } from '../components/editar-pagos-dialogo';
import { useEditarPagos, useMetodosPago, useVentasMarket } from '../ventas-market.api';
import type { DocumentoMarket, VentasMarketFiltros } from '../ventas-market.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Fecha local en `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: el mes en curso, como los campos del panel anterior. */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
  };
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Color del distintivo según el estado del comprobante, como el panel anterior. */
function varianteEstado(estado: string): 'success' | 'danger' | 'warning' | 'neutral' {
  const texto = estado.toLowerCase();

  if (texto.includes('registrad') || texto.includes('emitid')) return 'success';
  if (texto.includes('anulad') || texto.includes('rechazad')) return 'danger';
  if (texto.includes('pendiente')) return 'warning';

  return 'neutral';
}

export function VentasMarketPage() {
  useDocumentTitle('Logística · Ventas Market');

  const inicial = rangoPorDefecto();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [numero, setNumero] = useState('');
  const [cliente, setCliente] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<VentasMarketFiltros>({
    desde: inicial.desde,
    hasta: inicial.hasta,
    perPage: POR_PAGINA,
    page: 1,
  });
  const [editando, setEditando] = useState<DocumentoMarket | null>(null);

  const ventas = useVentasMarket(filtros);
  const metodos = useMetodosPago();
  const guardarPagos = useEditarPagos();
  // Referencia estable: si el catálogo aún no llegó, no se crea un array nuevo en
  // cada render (el diálogo se reiniciaría solo).
  const metodosPago = useMemo(() => metodos.data?.metodos_pago ?? [], [metodos.data]);

  const sinPermiso = ventas.error instanceof ApiError && ventas.error.status === 403;
  const lista = ventas.data?.documentos ?? [];
  const total = ventas.data?.total ?? 0;
  const pagina = ventas.data?.page ?? 1;
  const paginas = ventas.data?.paginas ?? 1;
  const diasMaximos = ventas.data?.dias_maximos_anular ?? 0;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      numero: numero.trim() || undefined,
      cliente: cliente.trim() || undefined,
      search: busqueda.trim() || undefined,
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Cierra el diálogo y limpia el error del guardado anterior. */
  function cerrarDialogo() {
    setEditando(null);
    guardarPagos.reset();
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <ShoppingCart className="size-6 text-brand-500" aria-hidden="true" />
          Ventas Market
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Comprobantes de los productos del market con sus productos, sus métodos de pago y el estado
          en el que los devolvió SUNAT.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {ventas.data ? <Badge variant="outline">{formatNumber(total)} documentos</Badge> : null}
        {diasMaximos > 0 ? (
          <Badge variant="neutral" title="Plazo configurado para anular un comprobante">
            Anulación: {diasMaximos} días
          </Badge>
        ) : null}
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>logistics</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>edición de los métodos de pago</strong> ya está disponible (sólo en los
        comprobantes cuyo turno no está verificado, porque el cierre de turno recalcula sus totales a
        partir de estos pagos). La <strong>anulación ante SUNAT</strong> depende del servicio externo
        de comprobantes, así que se portará junto con el resto de acciones de Documentos.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="vm-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="vm-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vm-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="vm-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vm-numero" className="text-sm font-medium text-ink-700">
                Número de documento
              </label>
              <input
                id="vm-numero"
                className={cn(CONTROL, 'w-40')}
                placeholder="Ej: BA07-365"
                value={numero}
                onChange={(evento) => setNumero(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vm-cliente" className="text-sm font-medium text-ink-700">
                Cliente
              </label>
              <input
                id="vm-cliente"
                className={cn(CONTROL, 'w-56')}
                placeholder="Nombre o documento"
                value={cliente}
                onChange={(evento) => setCliente(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vm-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="vm-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Producto, pago, estado…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                const nuevo = rangoPorDefecto();

                setDesde(nuevo.desde);
                setHasta(nuevo.hasta);
                setNumero('');
                setCliente('');
                setBusqueda('');
                setFiltros({ desde: nuevo.desde, hasta: nuevo.hasta, perPage: POR_PAGINA, page: 1 });
              }}
            >
              Mes en curso
            </Button>
          </div>

          {ventas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : ventas.error ? (
            <Alert variant="error" title="No se pudieron leer las ventas del market">
              {mensajeDe(ventas.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay ventas del market con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Documento</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Productos</th>
                    <th className="py-2">Pagos</th>
                    <th className="py-2 text-right">Total</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2 text-right">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((documento) => (
                    <tr key={documento.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <span className="block font-semibold text-brand-600">
                          {documento.numero || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {documento.fecha_emision ?? ''} {documento.tipo_documento}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {documento.turno_id ? `Turno #${documento.turno_id}` : 'Sin turno'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {documento.cliente || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {documento.documento_cliente || 'sin documento'}
                        </span>
                      </td>
                      <td className="py-3">
                        {documento.productos.length === 0 ? (
                          <span className="text-xs text-ink-300">Sin productos</span>
                        ) : (
                          <ul className="space-y-1.5">
                            {documento.productos.map((producto) => (
                              <li key={`${documento.id}-${producto.codigo_interno}`}>
                                <span className="block text-xs font-semibold text-ink-800">
                                  {producto.descripcion}
                                </span>
                                <span className="block text-[11px] text-ink-400">
                                  Cant: {formatNumber(producto.cantidad)} ·{' '}
                                  {formatCurrency(producto.precio_unitario)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="py-3">
                        {documento.pagos.length === 0 ? (
                          <span className="text-xs text-ink-300">Sin pagos</span>
                        ) : (
                          <ul className="space-y-1.5">
                            {documento.pagos.map((pago, indice) => (
                              <li key={`${documento.id}-pago-${indice}`}>
                                <span className="block text-xs font-semibold text-ink-800">
                                  {pago.metodo}: {formatCurrency(pago.monto)}
                                </span>
                                {pago.referencia ? (
                                  <span className="block text-[11px] text-ink-400">
                                    Ref: {pago.referencia}
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {formatCurrency(documento.total)}
                      </td>
                      <td className="py-3">
                        <Badge variant={varianteEstado(documento.estado)}>{documento.estado}</Badge>
                        <span className="mt-1 block text-[11px] text-ink-400">
                          {documento.dias_desde_emision !== null
                            ? `${documento.dias_desde_emision} días`
                            : ''}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          {documento.puede_editar_pago ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar métodos de pago"
                              aria-label={`Editar los métodos de pago de ${documento.numero}`}
                              onClick={() => {
                                guardarPagos.reset();
                                setEditando(documento);
                              }}
                            >
                              <Pencil className="size-4 text-brand-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Editar métodos de pago · el turno ya está verificado"
                              disabled
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}

                          {documento.puede_anular ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Anular documento · se habilita con el servicio de comprobantes"
                              disabled
                            >
                              <Lock className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={`Anular documento · ${documento.motivo_anular ?? 'el comprobante no está aceptado'}`}
                              disabled
                            >
                              <Ban className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} documentos · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {editando ? (
        <EditarPagosDialogo
          documento={editando}
          metodos={metodosPago}
          guardando={guardarPagos.isPending}
          error={
            guardarPagos.error ? mensajeDe(guardarPagos.error, 'No se pudieron guardar los pagos.') : null
          }
          onGuardar={(pagos) =>
            guardarPagos.mutate(
              { id: editando.id, pagos },
              { onSuccess: () => setEditando(null) },
            )
          }
          onCerrar={cerrarDialogo}
        />
      ) : null}
    </div>
  );
}
